import React, { useState } from 'react';

interface AdSpotProps {
  imageSrc?: string;
  audioSrc?: string;
}

const AdSpot: React.FC<AdSpotProps> = ({ 
  imageSrc = '/images/blc-spot-placeholder.jpg',
  audioSrc = '/audio/ozarkselec-spot.mp3'
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState<HTMLAudioElement | null>(null);

  const handlePlayAudio = () => {
    if (audioElement) {
      if (isPlaying) {
        audioElement.pause();
        setIsPlaying(false);
      } else {
        audioElement.play();
        setIsPlaying(true);
      }
    } else {
      const audio = new Audio(audioSrc);
      audio.onloadeddata = () => {
        audio.play();
        setIsPlaying(true);
        setAudioElement(audio);
      };
      audio.onended = () => {
        setIsPlaying(false);
      };
      audio.onerror = () => {
        console.error('Erreur lors du chargement de l\'audio');
        setIsPlaying(false);
      };
    }
  };

  return (
    <div className="r t-rec t-rec_pt_60 t-rec_pb_60" style={{paddingTop: '60px', paddingBottom: '60px', backgroundColor: 'var(--ozarks-light-gray)'}}>
      <div className="t-container">
        <div className="t-col t-col_12">
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 4px 20px var(--ozarks-shadow)',
            overflow: 'hidden',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              minHeight: '140px'
            }}>
              {/* Section gauche - Contenu */}
              <div style={{
                flex: '1',
                padding: '30px 40px',
                minWidth: '300px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  marginBottom: '15px'
                }}>
                  <img 
                    src="/images/blc-spot.jpg" 
                    alt="Radio BLC 90.1 FM" 
                    style={{
                      width: '80px',
                      height: '60px',
                      borderRadius: '8px',
                      objectFit: 'contain',
                      backgroundColor: 'white',
                      padding: '2px'
                    }}
                  />
                  <div>
                    <h3 style={{
                      color: 'var(--ozarks-blue-dark)',
                      margin: '0 0 5px 0',
                      fontWeight: 600,
                      fontSize: '18px'
                    }}>
                      Vous avez entendu parler de nous ?
                    </h3>
                    <p style={{
                      color: 'var(--ozarks-gray)',
                      margin: '0',
                      fontSize: '14px'
                    }}>
                      Découvrez notre spot publicitaire
                    </p>
                  </div>
                </div>
              </div>

              {/* Section droite - Player et Facebook */}
              <div style={{
                padding: '30px 40px',
                borderLeft: '1px solid #f0f0f0',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '20px',
                minWidth: '250px'
              }}>
                {/* Bouton Audio */}
                <button
                  onClick={handlePlayAudio}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: isPlaying ? 'var(--ozarks-blue-medium)' : 'var(--ozarks-blue-dark)',
                    color: 'white',
                    border: 'none',
                    padding: '15px 25px',
                    borderRadius: '50px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(27, 54, 93, 0.2)'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(27, 54, 93, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(27, 54, 93, 0.2)';
                  }}
                >
                  <div style={{
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {isPlaying ? (
                      <div style={{
                        display: 'flex',
                        gap: '2px'
                      }}>
                        <div style={{
                          width: '2px',
                          height: '10px',
                          backgroundColor: 'white',
                          borderRadius: '1px'
                        }}></div>
                        <div style={{
                          width: '2px',
                          height: '10px',
                          backgroundColor: 'white',
                          borderRadius: '1px'
                        }}></div>
                      </div>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="white" style={{marginLeft: '1px'}}>
                        <path d="M8 5v14l11-7z"/>
                      </svg>
                    )}
                  </div>
                  {isPlaying ? 'En cours...' : 'Écouter'}
                </button>

                {/* Lien Facebook */}
                <a
                  href="https://www.facebook.com/profile.php?id=61555968326960"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    backgroundColor: '#1877F2',
                    color: 'white',
                    textDecoration: 'none',
                    padding: '15px 25px',
                    borderRadius: '50px',
                    fontWeight: 600,
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 4px 15px rgba(24, 119, 242, 0.2)',
                    textAlign: 'center'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.backgroundColor = '#166FE5';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(24, 119, 242, 0.3)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.backgroundColor = '#1877F2';
                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(24, 119, 242, 0.2)';
                  }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="white">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  <div style={{textAlign: 'left'}}>
                    <div style={{fontSize: '12px', opacity: 0.8, marginBottom: '3px', fontWeight: 500}}>OZARKSELEC</div>
                    <div style={{fontSize: '14px', opacity: 1, fontWeight: 700}}>Actualités & Promotions</div>
                    <div style={{fontSize: '12px', opacity: 0.9}}>Abonnez-vous pour ne rien rater !</div>
                  </div>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdSpot;